import { useCallback, useEffect, useRef, useState } from "react";

import { useTransporte } from "../../lib/api/transporte";
import type { CVDocument } from "../../lib/document/model";
import type { Ruta } from "../../lib/document/paths";
import { valorParaRuta, type Sugerencia } from "../../lib/suggestions/types";

/**
 * Las sugerencias de la IA y lo que el usuario decide sobre cada una.
 *
 * Se generan UNA vez, con el documento tal como llegó del servidor. No se
 * regeneran al aceptar una: si lo hicieran, aceptar un cambio movería el resto
 * de la lista bajo el cursor, y revisar se volvería imposible.
 */
export const useSugerencias = (
  doc: CVDocument | null,
  // `unknown` y no `string`: hay campos del documento que son listas, y el
  // valor ya viene convertido por `valorParaRuta`.
  aplicar: (ruta: Ruta, valor: unknown) => void,
) => {
  const transporte = useTransporte();
  const [sugerencias, setSugerencias] = useState<Sugerencia[]>([]);
  const [cargando, setCargando] = useState(false);
  const [aviso, setAviso] = useState<string | undefined>();

  /**
   * De que CV ya se pidieron las sugerencias.
   *
   * Va en una ref, no en estado, y el efecto NO sale antes de tiempo por ella.
   * Con un `useState` booleano el panel se quedaba colgado en "Revisando..."
   * para siempre: StrictMode monta, desmonta y vuelve a montar; la limpieza del
   * primer montaje ponia `vigente = false`, asi que su `finally` ya no apagaba
   * el spinner, y el segundo montaje salia antes de llegar a el porque la
   * bandera ya estaba puesta. Nadie apagaba nada.
   */
  const pedidasPara = useRef<number | null>(null);

  useEffect(() => {
    if (!doc) return;
    if (pedidasPara.current === doc.id) return;
    pedidasPara.current = doc.id;

    setCargando(true);
    const cvId = doc.id;

    /**
     * Primero lo guardado, y solo si no hay nada se genera.
     *
     * Sin esto cada recarga volvia a pagar una llamada a Bedrock Y perdia lo
     * que la persona ya habia aceptado o descartado: al volver, el documento
     * conservaba los cambios aplicados pero las sugerencias reaparecian todas
     * como pendientes, pidiendo revisar dos veces lo mismo.
     */
    (async () => {
      const guardadas = await transporte.sugerenciasGuardadas(cvId).catch(() => null);
      if (guardadas?.sugerencias?.length) return { sugerencias: guardadas.sugerencias };
      return transporte.generarSugerencias(doc);
    })()
      .then((r) => {
        setSugerencias(r.sugerencias);
        setAviso("aviso" in r ? (r as { aviso?: string }).aviso : undefined);
      })
      .catch(() => {
        // Sin sugerencias el editor sigue siendo util: se puede editar a mano.
        // Por eso esto es un aviso y no un error que bloquee la pantalla.
        setAviso(
          "No pudimos revisar tu hoja de vida automáticamente. Puedes editarla igual.",
        );
      })
      // Sin condicion: el spinner se apaga pase lo que pase. Actualizar estado
      // de un componente desmontado es inocuo en React 18; dejarlo girando no.
      .finally(() => setCargando(false));
    // `transporte` no cambia en la vida del editor; esta en la lista por honestidad.
  }, [doc, transporte]);

  const marcar = useCallback(
    (id: string, estado: Sugerencia["estado"], final?: string) => {
      setSugerencias((prev) =>
        prev.map((s) => (s.id === id ? { ...s, estado, ...(final ? { final } : {}) } : s)),
      );
      // La UI no espera al servidor: la decision ya esta tomada y hacerle
      // esperar por registrarla solo la haria sentir lenta. Si falla, se pierde
      // la metrica, no la revision.
      if (doc) transporte.marcarSugerencia(doc.id, id, estado, final).catch(() => {});
    },
    [doc, transporte],
  );

  const aceptar = useCallback(
    (s: Sugerencia) => {
      if (s.sugerido) aplicar(s.ruta, valorParaRuta(s.ruta, s.sugerido));
      marcar(s.id, "aceptada");
    },
    [aplicar, marcar],
  );

  const rechazar = useCallback((s: Sugerencia) => marcar(s.id, "rechazada"), [marcar]);

  const editar = useCallback(
    (s: Sugerencia, texto: string) => {
      aplicar(s.ruta, valorParaRuta(s.ruta, texto));
      marcar(s.id, "editada", texto);
    },
    [aplicar, marcar],
  );

  /**
   * Aceptar todo lo que quede. Los avisos NO se marcan como aceptados: no hay
   * nada que aplicar en ellos, y darlos por resueltos escondería justamente lo
   * que la persona todavía tiene que escribir.
   */
  const aceptarRestantes = useCallback(() => {
    setSugerencias((prev) =>
      prev.map((s) => {
        if (s.estado !== "pendiente" || s.aviso) return s;
        if (s.sugerido) aplicar(s.ruta, valorParaRuta(s.ruta, s.sugerido));
        // Tambien se registra cada una: aceptar en bloque es una decision tan
        // real como aceptar de una en una, y si no se guardara, la metrica
        // solo veria a quien revisa despacio.
        if (doc) transporte.marcarSugerencia(doc.id, s.id, "aceptada").catch(() => {});
        return { ...s, estado: "aceptada" };
      }),
    );
  }, [aplicar, doc, transporte]);

  return { sugerencias, cargando, aviso, aceptar, rechazar, editar, aceptarRestantes };
};
